import { useState, useEffect, useRef, useMemo } from 'react';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { useLazyDownloadDocumentByKeyQuery } from '../../../../api/endpoints/documentContent';
import { useUploadAnnouncementDocumentMutation, useGetAnnouncementDocumentsByIdQuery, useDeleteAnnouncementDocumentMutation, usePublishAnnouncementMutation } from '../../../../api';
import DocumentDownloadService from '../../../../utils/documentDownloadService';

const useAnnouncementForm = (saveAnnouncement) => {
    const { th } = useLanguage();

    // ---- Dialog/Form state ----
    const [dialogVisible, setDialogVisible] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [currentAnnouncement, setCurrentAnnouncement] = useState(null);
    const [submitted, setSubmitted] = useState(false);

    // Fields
    const [id, setId] = useState(0);
    const [type, setType] = useState(null);
    const [typeName, setTypeName] = useState('');
    const [announcementType, setAnnouncementType] = useState(null);
    const [announcementTypeName, setAnnouncementTypeName] = useState('');
    const [brand, setBrand] = useState(th('announcement.defaultBrand'));
    const [announcementSubject, setAnnouncementSubject] = useState('');
    const [announcementDate, setAnnouncementDate] = useState(null);
    const [announcementContent, setAnnouncementContent] = useState('');
    const [status, setStatus] = useState(true);
    const [isActive, setIsActive] = useState(true);
    const [dealerType, setDealerType] = useState(0);
    const [country, setCountry] = useState('TR');
    const [announcementId, setAnnouncementId] = useState(null);
    const [domesticOrForeign, setDomesticOrForeign] = useState(null);

    const initialSnapshotRef = useRef(null);

    // Regional Manager
    const [userId, _setUserId] = useState(null);
    const setUserId = (value) => {
        _setUserId(Number.isFinite(Number(value)) ? Number(value) : null);
    };

    // Dealers
    const [selectedDealerIds, setSelectedDealerIds] = useState([]);

    // ---- Documents state & hooks ----
    const toastRef = useRef(null);
    const [downloadService] = useState(() => new DocumentDownloadService(toastRef));
    const [triggerDownloadByKey] = useLazyDownloadDocumentByKeyQuery();

    const getDocId = (d) => {
        const raw = d?.id ?? d?.documentId ?? d?.DocumentId ?? d?.Id;
        return Number.isFinite(Number(raw)) ? Number(raw) : null;
    };

    const getFileKey = (d) => d?.fileKey ?? d?.FileKey ?? d?.key ?? d?.Key ?? d?.documentKey ?? d?.DocumentKey ?? d?.storageKey ?? d?.StorageKey ?? null;

    const { data: documentsRaw, isLoading: isLoadingDocuments, refetch: refetchDocuments } = useGetAnnouncementDocumentsByIdQuery(announcementId, { skip: !announcementId });

    const documents = useMemo(() => {
        const list = Array.isArray(documentsRaw) ? documentsRaw : documentsRaw?.data ?? documentsRaw?.items ?? [];
        return dialogVisible && announcementId ? list : [];
    }, [documentsRaw, dialogVisible, announcementId]);

    const [uploadAnnouncementDocument] = useUploadAnnouncementDocumentMutation();
    const [deleteAnnouncementDocument] = useDeleteAnnouncementDocumentMutation();
    const [publishAnnouncement, { isLoading: isPublishing }] = usePublishAnnouncementMutation();

    const [updatedFiles, setUpdatedFiles] = useState([]);
    const [hasDocumentOperations, setHasDocumentOperations] = useState(false);

    const [isDownloadingDocument, setIsDownloadingDocument] = useState(false);
    const [isDeletingDocument, setIsDeletingDocument] = useState(false);
    const [isUpdatingDocument, setIsUpdatingDocument] = useState(false);

    // Dialog açılınca dokümanları tazele
    useEffect(() => {
        if (dialogVisible && announcementId) {
            refetchDocuments();
        }
    }, [dialogVisible, announcementId, refetchDocuments]);

    useEffect(() => {
        if (!dialogVisible) setUpdatedFiles([]);
    }, [dialogVisible]);

    // ---- Helpers ----
    const takeSnapshot = (overrides = {}) => {
        initialSnapshotRef.current = {
            type: overrides.type ?? type,
            announcementType: overrides.announcementType ?? announcementType,
            brand: overrides.brand ?? brand,
            announcementSubject: overrides.announcementSubject ?? announcementSubject,
            announcementDate: overrides.announcementDate ?? announcementDate,
            announcementContent: overrides.announcementContent ?? announcementContent,
            isActive: overrides.isActive ?? isActive,
            dealerType: overrides.dealerType ?? dealerType,
            userId: overrides.userId ?? userId,
            selectedDealerIds: overrides.selectedDealerIds ?? selectedDealerIds,
            domesticOrForeign: overrides.domesticOrForeign ?? domesticOrForeign
        };
    };
    const getIsDirty = () => {
        const snap = initialSnapshotRef.current;
        if (!snap) return true; // snapshot yoksa dirty say

        const dateStr = (d) => (d ? new Date(d).toISOString() : null);
        const sortedIds = (arr) => [...(arr ?? [])].sort((a, b) => a - b).join(',');

        return (
            snap.type !== type ||
            snap.announcementType !== announcementType ||
            snap.brand !== brand ||
            snap.announcementSubject !== announcementSubject ||
            dateStr(snap.announcementDate) !== dateStr(announcementDate) ||
            snap.announcementContent !== announcementContent ||
            snap.isActive !== isActive ||
            snap.dealerType !== dealerType ||
            snap.userId !== userId ||
            sortedIds(snap.selectedDealerIds) !== sortedIds(selectedDealerIds) ||
            snap.domesticOrForeign !== domesticOrForeign
        );
    };

    const resetForm = () => {
        setId(0);
        setType(null);
        setTypeName('');
        setAnnouncementType(null);
        setAnnouncementTypeName('');
        setBrand(th('announcement.defaultBrand'));
        setAnnouncementSubject('');
        setAnnouncementDate(new Date()); // Bugünün tarihi
        setAnnouncementContent('');
        setStatus(true);
        setIsActive(true);
        _setUserId(null);
        setSelectedDealerIds([]);
        setDealerType(0);
        setCountry('TR');
        setAnnouncementId(null);
        setUpdatedFiles([]);
        setHasDocumentOperations(false);
        setDomesticOrForeign(null);
    };

    const setFormData = (a) => {
        setType(a?.type ?? null);
        setTypeName(a?.typeName ?? '');
        setAnnouncementType(a?.announcementType ?? null);
        setAnnouncementTypeName(a?.announcementTypeName ?? '');
        setBrand(a?.brand ?? th('announcement.defaultBrand'));
        setAnnouncementSubject(a?.announcementSubject ?? '');
        setAnnouncementDate(a?.announcementDate ? new Date(a.announcementDate) : null);
        setAnnouncementContent(a?.announcementContent ?? '');
        setStatus(typeof a?.status === 'number' ? a.status : !!a?.status);
        setIsActive(typeof a?.isActive === 'number' ? a.isActive === 1 : !!a?.isActive);
        setAnnouncementId(Number(a?.id ?? a?.Id) || null);
        setDealerType(Number.isFinite(Number(a?.dealerType)) ? Number(a.dealerType) : 0);

        const uid = Number(a?.managerUserId ?? a?.ManagerUserId ?? a?.userId);
        _setUserId(Number.isFinite(uid) ? uid : null);

        const ids = (a?.announcementDealers ?? a?.AnnouncementDealers ?? a?.dealers ?? []).map((d) => Number(d?.dealerId ?? d?.erpDealerId ?? d)).filter(Number.isFinite);
        setSelectedDealerIds(ids);
    };

    const handleAddNew = () => {
        setEditMode(false);
        setCurrentAnnouncement(null);
        resetForm();
        setSubmitted(false);
        setDialogVisible(true);
        initialSnapshotRef.current = null;
    };

    const handleEdit = (a) => {
        setEditMode(true);
        setCurrentAnnouncement(a ?? null);
        setSubmitted(false);
        if (a) {
            setFormData(a);
            // setFormData'daki değerleri snapshot'a al
            takeSnapshot({
                type: a?.type ?? null,
                announcementType: a?.announcementType ?? null,
                brand: a?.brand ?? th('announcement.defaultBrand'),
                announcementSubject: a?.announcementSubject ?? '',
                announcementDate: a?.announcementDate ? new Date(a.announcementDate) : null,
                announcementContent: a?.announcementContent ?? '',
                isActive: typeof a?.isActive === 'number' ? a.isActive === 1 : !!a?.isActive,
                dealerType: Number.isFinite(Number(a?.dealerType)) ? Number(a.dealerType) : 0,
                userId: (() => {
                    const uid = Number(a?.managerUserId ?? a?.ManagerUserId ?? a?.userId);
                    return Number.isFinite(uid) ? uid : null;
                })(),
                selectedDealerIds: (a?.announcementDealers ?? a?.AnnouncementDealers ?? a?.dealers ?? []).map((d) => Number(d?.dealerId ?? d?.erpDealerId ?? d)).filter(Number.isFinite),
                domesticOrForeign: a?.domesticOrForeign ?? null
            });
        }
        setDialogVisible(true);
    };

    const hideDialog = () => setDialogVisible(false);

    useEffect(() => {
        if (!dialogVisible) {
            resetForm();
            setEditMode(false);
            setCurrentAnnouncement(null);
            setSubmitted(false);
        }
    }, [dialogVisible]);

    const isValid = () => type !== null && announcementType !== null && !!brand?.trim() && !!announcementSubject?.trim() && announcementDate !== null;

    // ---- Document ops ----
    const onEditFilesChange = (files) => setUpdatedFiles(files || []);
    const onRemoveFile = (index) => setUpdatedFiles((prev) => prev.filter((_, i) => i !== index));

    const uploadFiles = async (idOverride) => {
        const realId = idOverride ?? announcementId;
        if (!realId || updatedFiles.length === 0) return;

        for (const item of updatedFiles) {
            const f = item?.rawFile || item?.file || item;
            await uploadAnnouncementDocument({
                AnnouncementId: realId,
                file: f,
                description: f?.name || item?.name || 'attachment'
            }).unwrap();
        }
        setHasDocumentOperations(true);
        await refetchDocuments();
        setUpdatedFiles([]);
    };

    const onDeleteDocument = async (doc) => {
        const docId = getDocId(doc);
        if (!announcementId || !docId) return;
        //th('announcement.defaultBrand')
        if (!window.confirm('${doc.fileName}' & th('announcement.deleteDocumentMessage'))) return;
        try {
            setIsDeletingDocument(true);
            await deleteAnnouncementDocument({
                AnnouncementId: announcementId,
                DocumentId: docId
            }).unwrap();
            setHasDocumentOperations(true);
            await refetchDocuments();
            toastRef.current?.show({ severity: 'success', detail: th('announcement.deleteDocumentSuccessMessage'), life: 2500 });
        } catch (e) {
            toastRef.current?.show({ severity: 'error', detail: th('announcement.deleteErrorMessage'), life: 3000 });
        } finally {
            setIsDeletingDocument(false);
        }
    };

    const onUpdateDocument = async (doc, newFile) => {
        const docIdUp = getDocId(doc);
        if (!announcementId || !docIdUp || !newFile) return;
        if (!window.confirm('${doc.fileName}' & th('announcement.updateDocumentMessage'))) return;
        try {
            setIsUpdatingDocument(true);
            await deleteAnnouncementDocument({ AnnouncementId: announcementId, DocumentId: docIdUp }).unwrap();
            await uploadAnnouncementDocument({
                AnnouncementId: announcementId,
                file: newFile,
                description: newFile.name
            }).unwrap();
            setHasDocumentOperations(true);
            await refetchDocuments();
            toastRef.current?.show({ severity: 'success', detail: th('announcement.updateDocumentSuccessMessage'), life: 2500 });
        } catch {
            toastRef.current?.show({ severity: 'error', detail: th('announcement.updateErrorMessage'), life: 3000 });
        } finally {
            setIsUpdatingDocument(false);
        }
    };

    const onDownloadDocument = async (doc) => {
        const key = getFileKey(doc);
        if (!key) {
            toastRef.current?.show({ severity: 'warn', detail: th('announcement.fileKeyErrorMessage'), life: 2500 });
            return;
        }
        try {
            setIsDownloadingDocument(true);
            await downloadService.downloadByKey(triggerDownloadByKey, key, doc, { successMessage: th('announcement.downloadDocumentSuccessMessage'), errorMessage: 'announcement.downloadDocumentErrorMessage' });
        } finally {
            setIsDownloadingDocument(false);
        }
    };

    const setToastCallback = (extToastRef) => {
        if (extToastRef?.current) {
            toastRef.current = extToastRef.current;
            downloadService.toastRef = extToastRef;
        }
    };

    // ---- Submit ----
    const handleSubmit = async () => {
        setSubmitted(true);
        if (!isValid()) return false;

        try {
            const id = announcementId ?? currentAnnouncement?.id ?? currentAnnouncement?.Id ?? null;
            const toIntOrNull = (v) => (v === '' || v == null ? null : Number.isFinite(Number(v)) ? Number(v) : null);

            const payload = {
                ...(editMode && id ? { id: Number(id) } : {}),
                type: Number(type),
                announcementType: Number(announcementType),
                brand: (brand ?? '').trim(),
                managerUserId: toIntOrNull(userId),
                dealerType: toIntOrNull(dealerType),
                announcementSubject: (announcementSubject ?? '').trim(),
                announcementDate: announcementDate ? new Date(announcementDate).toISOString() : null,
                announcementContent: (announcementContent ?? '').trim(),
                status: typeof status === 'number' ? status : status ? 1 : 0,
                isActive: isActive ? 1 : 0,
                announcementDealers: (selectedDealerIds ?? []).filter((x) => x != null).map((id2) => ({ dealerId: String(id2) }))
            };

            const ok = await saveAnnouncement(payload, editMode, id);
            if (!ok) return false;

            const finalId = id || ok?.id || ok?.data?.id || ok?.result?.id || ok?.payload?.id || ok?.data?.data?.id || ok?.Data?.Id || null;

            if (updatedFiles.length > 0) {
                const realId = finalId || announcementId;
                if (realId && !announcementId) setAnnouncementId(realId);
                await uploadFiles(realId);
            }

            hideDialog();
            return true;
        } catch (err) {
            console.error('Error in form submission:', err);
            return false;
        }
    };

    const handlePublish = async () => {
        const id = Number(announcementId ?? currentAnnouncement?.id ?? currentAnnouncement?.Id);
        if (!Number.isFinite(id)) {
            toastRef.current?.show({ severity: 'warn', detail: th('announcement.invalidData'), life: 2500 });
            return;
        }
        try {
            // 1. Form değiştiyse önce kaydet
            const dirty = getIsDirty();
            if (dirty) {
                const toIntOrNull = (v) => (v === '' || v == null ? null : Number.isFinite(Number(v)) ? Number(v) : null);
                const payload = {
                    id: Number(id),
                    type: Number(type),
                    announcementType: Number(announcementType),
                    brand: (brand ?? '').trim(),
                    managerUserId: toIntOrNull(userId),
                    dealerType: toIntOrNull(dealerType),
                    announcementSubject: (announcementSubject ?? '').trim(),
                    announcementDate: announcementDate ? new Date(announcementDate).toISOString() : null,
                    announcementContent: (announcementContent ?? '').trim(),
                    status: typeof status === 'number' ? status : status ? 1 : 0,
                    isActive: isActive ? 1 : 0,
                    announcementDealers: (selectedDealerIds ?? []).filter((x) => x != null).map((id2) => ({ dealerId: String(id2) }))
                };

                const saveOk = await saveAnnouncement(payload, true, id);
                if (!saveOk) {
                    toastRef.current?.show({ severity: 'error', detail: th('announcement.updateErrorMessage'), life: 3000 });
                    return;
                }
            }

            // 2. Dosya varsa yükle
            if (updatedFiles.length > 0) {
                await uploadFiles(id);
            }

            // 3. Yayınla
            await publishAnnouncement({ id }).unwrap();
            setStatus(1);
            toastRef.current?.show({ severity: 'success', detail: th('announcement.publishSuccessMessage'), life: 2500 });
            hideDialog();
        } catch (e) {
            toastRef.current?.show({ severity: 'error', detail: th('announcement.publishErrorMessage'), life: 3000 });
        }
    };

    const handleSafeDownload = async (doc, event) => {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }
        try {
            setIsDownloadingDocument(true);
            await onDownloadDocument(doc);
            toastRef.current?.show({
                severity: 'success',
                detail: th('announcement.downloadDocumentSuccessMessage'),
                life: 3000
            });
        } catch (error) {
            toastRef.current?.show({
                severity: 'error',
                detail: th('announcement.downloadDocumentErrorMessage'),
                life: 3000
            });
        } finally {
            setIsDownloadingDocument(false);
        }
    };

    const renderDialogFooter = () => (
        <div className="flex justify-content-end gap-2">
            {editMode && (
                <button className="p-button p-component p-button-success" onClick={handlePublish} type="button" disabled={isPublishing}>
                    <span className={`p-button-icon p-button-icon-left ${isPublishing ? 'pi pi-spin pi-spinner' : 'pi pi-bullhorn'}`}></span>
                    <span className="p-button-label">{th('announcement.formLabels.publish')}</span>
                </button>
            )}
            <button className="p-button p-component p-button-outlined" onClick={hideDialog} type="button">
                <span className="p-button-icon p-button-icon-left pi pi-times"></span>
                <span className="p-button-label">{th('common.cancel')}</span>
            </button>
            <button className="p-button p-component" onClick={handleSubmit} type="button">
                <span className="p-button-icon p-button-icon-left pi pi-check"></span>
                <span className="p-button-label">{th('common.save')}</span>
            </button>
        </div>
    );

    return {
        // dialog
        dialogVisible,
        editMode,
        currentAnnouncement,
        submitted,
        hideDialog,
        handleAddNew,
        handleEdit,
        handleSubmit,
        renderDialogFooter,

        // fields
        type,
        typeName,
        announcementType,
        announcementTypeName,
        brand,
        announcementSubject,
        announcementDate,
        announcementContent,
        status,
        isActive,
        dealerType,
        country,
        setType,
        setTypeName,
        setAnnouncementType,
        setAnnouncementTypeName,
        setBrand,
        setAnnouncementSubject,
        setAnnouncementDate,
        setAnnouncementContent,
        setStatus,
        setIsActive,
        setDealerType,
        setCountry,

        // manager
        userId,
        setUserId,

        // dealers
        selectedDealerIds,
        setSelectedDealerIds,

        // documents
        documents,
        isLoadingDocuments,
        updatedFiles,
        onEditFilesChange,
        onRemoveFile,
        onDownloadDocument,
        onDeleteDocument,
        onUpdateDocument,
        isDownloadingDocument,
        isDeletingDocument,
        isUpdatingDocument,
        uploadFiles,
        hasDocumentOperations,
        handleSafeDownload,
        setToastCallback,

        // publish
        handlePublish,
        isPublishing,
        domesticOrForeign,
        setDomesticOrForeign
    };
};

export default useAnnouncementForm;
